import type { Line } from '../../../lottery-purchases/types';
import { getMatchedNumbers, hasMatchedNumbers } from '../../liveDraw/getMatchedNumbers';
import type { DrawnNumbers } from '../../liveDraw/getRevealedNumbers';
import type { YourNumbersMatching } from './YourNumbersLine/YourNumbersLine';

type GetYourNumbersLineMatchingInput = {
  line: Line;
  /**
   * Revealed prefix from `useLiveDraw().revealedNumbers`. When set, matching is derived from these
   * balls only — the caller's `matching` is ignored. Prefix wins, always.
   */
  revealedNumbers?: DrawnNumbers;
  matching?: YourNumbersMatching;
  prize?: number;
  isDrawComplete: boolean;
};

/**
 * Presentation wrapper around {@link getMatchedNumbers} for a Your Numbers line.
 *
 * The sequencer owns the revealed prefix. This only decides whether the line should light up,
 * dim, or stay neutral, and whether Match! should show.
 */
function getYourNumbersLineMatching({ line, revealedNumbers, matching, prize, isDrawComplete }: GetYourNumbersLineMatchingInput): YourNumbersMatching | undefined {
  if (revealedNumbers === undefined) {
    return matching;
  }

  const matched = getMatchedNumbers(line, revealedNumbers);
  const hasMatch = hasMatchedNumbers(matched);

  // Empty matching with isHighlighted: false is visually the same as no matching, but
  // YourNumbersLine still renders an sr-only line_matches count of 0. Stay undefined until a
  // ball has matched so that count is not announced during the reveal.
  if (!hasMatch && !isDrawComplete) {
    return undefined;
  }

  return {
    numbers: matched.numbers,
    extraNumbers: line.extraNumbers ? matched.extraNumbers : undefined,
    isHighlighted: hasMatch && (!isDrawComplete || (prize ?? 0) > 0),
  };
}

export { getYourNumbersLineMatching };
export type { GetYourNumbersLineMatchingInput };
